import { useAtomValue } from 'jotai';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  selectedStructureRiskAssessment,
  structureTemplate as structureTemplateState,
} from '@/components/Analysis/state';
import { LoadingButton } from '@/components/shared/LoadingButton';
import {
  TemplateDataInput,
  useCreateRiskAssessmentDetailsMutation,
  StructureForRiskAssessmentDocument,
  StructureForRiskAssessmentQuery,
} from '@/__generated__/graphql';
import { CustomField } from '@/components/shared/CustomField';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';

interface Props {
  onSubmit?: () => void;
  onCancel?: () => void;
  setLoader?: (argument0: boolean) => void;
}

export const StructureSetup = ({ onSubmit, onCancel, setLoader }: Props) => {
  const structure = useAtomValue(selectedStructureRiskAssessment);
  const [structureClone, setStructureClone] =
    useState<StructureForRiskAssessmentQuery['structure']>();
  const structureTemplate = useAtomValue(structureTemplateState);
  const [currentTemplateData, setCurrentTemplateData] = useState<TemplateDataInput[]>([]);

  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });

  const [createRiskAssessmentDetails, { loading: createRiskAssessmentDetailsIsLoading }] =
    useCreateRiskAssessmentDetailsMutation({
      onCompleted: (result) => {
        showSnackBar('The platform has been set successfully', 'success');
        if (setLoader) {
          setLoader(false);
        }
        if (onSubmit && result.createRiskAssessmentDetails) onSubmit();
      },
      refetchQueries: [StructureForRiskAssessmentDocument],
    });

  useEffect(() => {
    if (setLoader) {
      setLoader(createRiskAssessmentDetailsIsLoading);
    }
  }, [createRiskAssessmentDetailsIsLoading, setLoader]);

  useEffect(() => {
    if (structureTemplate && structureClone?.templateData) {
      const mappedTemplateData = structureClone.templateData.map((td) => {
         
        const { id, templateFieldId, value } = td;
        return { id: templateFieldId ?? id, value: value! };
      });
      setCurrentTemplateData(mappedTemplateData);
    }
  }, [structureClone?.templateData, structureTemplate]);

  useEffect(() => {
    if (structure) {
      const clone: StructureForRiskAssessmentQuery['structure'] = JSON.parse(
        JSON.stringify(structure)
      );
      setStructureClone(clone);
    }
  }, [structure]);

  const canNotSubmit = useMemo(() => {
    if (currentTemplateData) {
      const fieldsAreMissing =
        currentTemplateData.length !== structureTemplate?.fields.length ||
        currentTemplateData.some((field) => !field.value);
      if (fieldsAreMissing) {
        return true;
      }
      return false;
    }
    return true;
  }, [currentTemplateData, structureTemplate?.fields.length]);

  const submitFields = () => {
    // if can submit
    if (!canNotSubmit) {
      createRiskAssessmentDetails({
        variables: {
          input: {
            structureId: structureClone?.id ?? '',
            templateId: structureClone?.template?.id ?? '',
            templateData: currentTemplateData,
          },
        },
      });
    }
  };

  const changeField = useCallback((value: string, fieldId: string) => {
    if (!fieldId) return;
    const result = {
      id: fieldId,
      value,
    };
    setCurrentTemplateData((current) => {
      const currentInfoCopy = current || [];
      // Check if the template field id already exists
      const index = currentInfoCopy.findIndex((fields) => fields.id === fieldId);
      // if exists, mutate the copy and return
      if (index !== -1) {
        currentInfoCopy[index] = result;
        return [...currentInfoCopy];
      }
      // if not, add it into the copy and return
      return [...currentInfoCopy, result];
    });
  }, []);

  return (
    <Stack direction="column" spacing={2}>
      <Stack pt={1} spacing={2}>
        {structureTemplate?.fields
          .filter((field) => !field.isHidden)
          .map((field) => {
            const fieldId = field?.id;
            const fieldType = field?.type === 'MULTI_SELECT' ? 'SINGLE_SELECT' : field?.type; // TODO: implement MULTI_SELECT for CustomField
            const fieldOptions = field?.options;
            const fieldName = field?.name;
            const fieldValue = structure?.templateData?.find((td) => td.id === field.id)?.value;
            return (
              <CustomField
                key={fieldName}
                fieldType={fieldType}
                fieldOptions={fieldOptions}
                fieldName={fieldName}
                initialValue={fieldValue ?? ''}
                onChange={(value) => changeField(value, fieldId)}
              />
            );
          })}
      </Stack>
      <Stack direction="row" justifyContent="flex-end" spacing={1}>
        <Button onClick={onCancel} variant="outlined">
          Cancel
        </Button>
        <LoadingButton
          disabled={canNotSubmit}
          onClick={submitFields}
          loading={createRiskAssessmentDetailsIsLoading}
        >
          Submit
        </LoadingButton>
      </Stack>
    </Stack>
  );
};
